<script setup>
import { ref, onMounted, watch } from 'vue'
import { useElementSize, useMouseInElement } from '@vueuse/core'
import { gsap } from 'gsap'
import useUtils from '@/composables/useUtils'
import textBoxBG from '@/assets/img/text-box-bg.svg'

const container = ref(null)
const circleDark = ref(null)
const circleLight = ref(null)
const circleDimensions = {
  w: 0,
  h: 0,
}
const windowSize = ref(window.innerWidth)
const { device } = useUtils()
const { width, height } = useElementSize(container)
const { x, y, elementX, elementY, isOutside } = useMouseInElement(container)
const props = defineProps({
  blobSize: {
    type: Number,
    default: 1.2,
  },
  theme: {
    type: String,
    default: 'dark',
  },
  staticBg: {
    type: String,
    default: '',
    required: false,
  },
  mobileBg: {
    type: String,
    default: '',
    required: false,
  },
})

const setCircleStyles = () => {
  circleDimensions.value = {
    w:
      width.value *
      (device.value !== 'desktop' ? props.blobSize * 1.6 : props.blobSize),
    h:
      width.value *
      (device.value !== 'desktop' ? props.blobSize * 1.6 : props.blobSize),
  }
  const positive = [1, 4, 9, 7, 0]
  gsap.set(circleDark.value, {
    x: width.value / 2 - circleDimensions.value.w / 2,
    y: height.value / 2 - circleDimensions.value.h / 2,
    width: circleDimensions.value.w + 'px',
    height: circleDimensions.value.h + 'px',
    filter: `blur(${width.value * (((100 * 50) / width.value) * 0.01)}px)`,
  })
  gsap.set(circleLight.value, {
    x: width.value / 2 - circleDimensions.value.w / 2,
    y: height.value / 2 - circleDimensions.value.h / 2,
    width: circleDimensions.value.w + 'px',
    height: circleDimensions.value.h + 'px',
    filter: `blur(${width.value * (((100 * 50) / width.value) * 0.01)}px)`,
  })
  gsap.to(circleDark.value, {
    rotate: `${
      positive.some((num) => num === Math.floor(Math.random() * 11)) ? '' : '-'
    }${360}`,
    duration: `${
      positive.some((num) => num === Math.floor(Math.random() * 11))
        ? '15'
        : '25'
    }`,
    repeat: -1,
    ease: 'none',
  })
  gsap.to(circleLight.value, {
    rotate: `${
      positive.some((num) => num === Math.floor(Math.random() * 11)) ? '' : '-'
    }${360}`,
    duration: `${
      positive.some((num) => num === Math.floor(Math.random() * 11))
        ? '15'
        : '25'
    }`,
    repeat: -1,
    ease: 'none',
  })
}

watch([windowSize], () => {
  circleDimensions.value = {
    w:
      width.value *
      (device.value !== 'desktop' ? props.blobSize * 1.6 : props.blobSize),
    h:
      width.value *
      (device.value !== 'desktop' ? props.blobSize * 1.6 : props.blobSize),
  }
  gsap.set(circleDark.value, {
    x: width.value / 2 - circleDimensions.value.w / 2,
    y: height.value / 2 - circleDimensions.value.h / 2,
    width: circleDimensions.value.w + 'px',
    height: circleDimensions.value.h + 'px',
  })
  gsap.set(circleLight.value, {
    x: width.value / 2 - circleDimensions.value.w / 2,
    y: height.value / 2 - circleDimensions.value.h / 2,
    width: circleDimensions.value.w + 'px',
    height: circleDimensions.value.h + 'px',
  })
})

watch([x, y], () => {
  if (props.staticBg) return
  if (!circleDark.value || !circleLight.value) return

  if (isOutside.value) {
    gsap.to(circleDark.value, {
      x: width.value / 2 - circleDimensions.value.w / 2,
      y: height.value / 2 - circleDimensions.value.h / 2,
      ease: 'power4.easeInOut',
      duration: 3,
    })
    gsap.to(circleLight.value, {
      x: width.value / 2 - circleDimensions.value.w / 2,
      y: height.value / 2 - circleDimensions.value.h / 2,
      ease: 'power4.easeInOut',
      duration: 3,
    })
  } else {
    const xPercentageFromCenter =
      (elementX.value - width.value / 2) / (width.value / 2)
    const yPercentageFromCenter =
      (elementY.value - height.value / 2) / (height.value / 2)

    gsap.to(circleDark.value, {
      x:
        width.value / 2 -
        circleDimensions.value.w / 2 +
        xPercentageFromCenter * 120,
      y:
        height.value / 2 -
        circleDimensions.value.h / 2 +
        yPercentageFromCenter * 120,
      ease: 'power1.easeInOut',
      duration: 1,
    })
    gsap.to(circleLight.value, {
      x:
        width.value / 2 -
        circleDimensions.value.w / 2 +
        xPercentageFromCenter * 120,
      y:
        height.value / 2 -
        circleDimensions.value.h / 2 +
        yPercentageFromCenter * 120,
      ease: 'power1.easeInOut',
      duration: 1,
    })
  }
})

onMounted(async () => {
  if (props.staticBg) return
  window.addEventListener('resize', () => {
    windowSize.value = window.innerWidth
  })
  setCircleStyles()
})
</script>

<template>
  <div
    ref="container"
    class="blur-card"
    :style="`${staticBg ? `background-image: url('${staticBg}');` : ''} ${device !== 'desktop' ? `background-image: url('${textBoxBG}');` : ''}`"
    :class="`${props.theme === 'dark' && 'dark'}`">
    <svg
      v-show="props.theme === 'dark' && !staticBg && device === 'desktop'"
      id="Layer_1"
      ref="circleDark"
      data-name="Layer 1"
      class="circle"
      viewBox="0 0 197.39 194.11">
      <defs>
        <radialGradient
          id="radial-gradient"
          cx="98.45"
          cy="96.07"
          fx="98.45"
          fy="96.07"
          r="88.18"
          gradientTransform="translate(-25.59 -25.71) scale(1.26 1.28)"
          gradientUnits="userSpaceOnUse">
          <stop
            offset="1"
            stop-color="#567bfd" />
        </radialGradient>
      </defs>
      <path
        d="M162.62,18.97c18.25,11.08,32.98,29.55,34.61,48.98,1.62,19.43-9.87,39.82-17.98,60.07-8.11,20.39-12.71,40.77-25.28,52.68-12.57,11.9-33.12,15.32-51.64,12.45-18.66-2.74-35.15-11.77-47.85-23.53-12.71-11.9-21.63-26.54-32.04-44.19C12.03,107.63.14,86.97,0,64.95-.13,42.92,11.63,19.66,30.42,8.71,49.07-2.24,74.76-.73,98.41,1.87c23.79,2.46,45.83,6.02,64.21,17.1Z"
        style="fill: url(#radial-gradient); stroke-width: 0px" />
      <circle
        cx="98.69"
        cy="92.56"
        r="50.2"
        style="fill: #567bfd; stroke-width: 0px" />
    </svg>
    <svg
      v-show="props.theme === 'light' && !staticBg && device === 'desktop'"
      id="Layer_1_light"
      ref="circleLight"
      data-name="Layer 1 light"
      class="circle"
      viewBox="0 0 197.39 194.11">
      <defs>
        <radialGradient
          id="radial-gradient-light"
          cx="98.45"
          cy="96.07"
          fx="98.45"
          fy="96.07"
          r="88.18"
          gradientTransform="translate(-25.59 -25.71) scale(1.26 1.28)"
          gradientUnits="userSpaceOnUse">
          <stop
            offset="1"
            stop-color="#B7F4F0" />
        </radialGradient>
      </defs>
      <path
        d="M162.62,18.97c18.25,11.08,32.98,29.55,34.61,48.98,1.62,19.43-9.87,39.82-17.98,60.07-8.11,20.39-12.71,40.77-25.28,52.68-12.57,11.9-33.12,15.32-51.64,12.45-18.66-2.74-35.15-11.77-47.85-23.53-12.71-11.9-21.63-26.54-32.04-44.19C12.03,107.63.14,86.97,0,64.95-.13,42.92,11.63,19.66,30.42,8.71,49.07-2.24,74.76-.73,98.41,1.87c23.79,2.46,45.83,6.02,64.21,17.1Z"
        style="fill: url(#radial-gradient-light); stroke-width: 0px" />
      <circle
        cx="98.69"
        cy="92.56"
        r="50.2"
        style="fill: #b7f4f0; stroke-width: 0px" />
    </svg>
    <div class="content">
      <slot></slot>
    </div>
  </div>
</template>

<style scoped>
.blur-card {
  border-radius: 16px;
  background: rgb(183, 244, 240);
  background-image: radial-gradient(
    circle,
    rgba(183, 244, 240, 1) 56%,
    rgba(255, 248, 86, 1) 200%
  );
  background-size: cover;
  background-repeat: no-repeat;
  overflow: hidden;
  position: relative;
  width: 100%;
  height: 100%;
  &.dark {
    background-image: radial-gradient(
      circle,
      rgba(183, 244, 240, 1) 86%,
      rgba(255, 248, 86, 1) 200%
    );
  }
  .circle {
    position: absolute;
    transform-origin: center;
    z-index: 0;
  }
  .content {
    position: relative;
    z-index: 1;
  }
}
</style>
